import { useState } from 'react';
import { line, Size, t } from '@bms2/shared';
import { AlertBanner, Select } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, del, post, useApi } from './api';
import styles from './RecordPage.module.css';

/** One sub-skill this person is recorded as able to do, as the read answers it. */
interface HeldSubSkillRow {
  id: string;
  subSkillId: string;
  subSkillName: string;
  skillName: string;
  notedByName: string | null;
}

/** One skill the person holds, whole or by the sub-skills recorded under it (decision 0614). */
interface HeldGroup {
  skillId: string;
  skillName: string;
  wholeRowId: string | null;
  subSkills: HeldSubSkillRow[];
}

/** One skill the picker offers, with the sub-skills beneath it; a skill with none offers the whole alone. */
interface SkillOption {
  skillId: string;
  skillName: string;
  subSkills: { id: string; name: string }[];
}

export interface SkillModalProps {
  open: boolean;
  personId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

/** What the second level offers where the whole skill is chosen, rather than a sub-skill's own id. */
const WHOLE = 'whole';

export function skillHoldingPath(personId: string): string {
  return `${PAGES_PATH}/people/${personId}/skills`;
}

/**
 * The Person page's Skills tile writes here (decisions 0464, 0614): what
 * somebody is recorded as able to do. 🔒 The picker is two levels — the skill
 * first, then the whole skill or one of its sub-skills — because a skill with
 * no sub-skills configured is still a skill somebody does, and the tile had no
 * way to say so (product owner, 2026-09-21).
 *
 * 💰 It is a statement somebody made, not a certificate: it proves nothing and
 * expires on no date, which the hint says on the screen. A skill held whole
 * silences the dispatch warning for every sub-skill under it, and the hint says
 * that too, because it is the reversible half of the claim.
 *
 * 🔨 A row is recorded and withdrawn, never edited, so this modal has no Save:
 * each press writes or removes one row and the tile reloads behind it.
 */
export function SkillModal({ open, personId, apiBaseUrl = '', onCancel, onSaved }: SkillModalProps) {
  const answer = useApi<{ groups: HeldGroup[]; options: SkillOption[] }>(open ? skillHoldingPath(personId) : null, apiBaseUrl);
  const [skillId, setSkillId] = useState('');
  const [choice, setChoice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const groups = answer.state.kind === 'ready' ? answer.state.data.groups : [];
  const options = answer.state.kind === 'ready' ? answer.state.data.options : [];
  const chosenSkill = options.find((option) => option.skillId === skillId) ?? null;
  const heldHere = groups.find((group) => group.skillId === skillId) ?? null;

  const wrote = (outcome: { ok: true } | { ok: false; message: string }) => {
    if (!outcome.ok) return setError(outcome.message);
    setError(null);
    answer.reload();
    onSaved();
  };

  const record = async () => {
    if (!skillId) return setError(t('skill.pickSkillOne'));
    if (!choice) return setError(t('skill.pickWhat'));
    const body = choice === WHOLE ? { skillId } : { skillId, subSkillId: choice };
    const outcome = await post(skillHoldingPath(personId), body, apiBaseUrl);
    setChoice('');
    wrote(outcome);
  };

  const withdraw = async (kind: 'whole' | 'sub', rowId: string) => wrote(await del(`${skillHoldingPath(personId)}/${kind}/${rowId}`, apiBaseUrl));

  /**
   * 🔨 The second level is emptied when the first changes: a sub-skill chosen
   * under one skill is not a choice under the next one, and leaving it standing
   * would post a pairing nobody picked.
   */
  const pickSkill = (next: string) => {
    setSkillId(next);
    setChoice('');
  };

  return (
    <Modal
      open={open}
      title={t('skill.title')}
      onCancel={onCancel}
      dataModal="person-skill"
      actions={[
        { kind: 'primary', label: t('settings.add'), onAct: () => void record(), disabled: !skillId || !choice },
        { kind: 'cancel', label: t('modal.close'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={styles.hint}>{t('skill.hint')}</p>
      <ul className={styles.plainList} data-person-skills="true">
        {groups.length === 0 ? <li className={styles.hint}>{t('skill.none')}</li> : null}
        {groups.map((group) => (
          <li key={group.skillId} data-person-skill={group.skillName}>
            <span>{group.skillName}</span>
            <ul className={styles.plainList}>
              {group.wholeRowId ? (
                <li data-person-skill-whole={group.skillName}>
                  <span>{t('skill.wholeSkill')}</span>
                  <button type="button" className={styles.clearFlag} data-withdraw-skill={group.skillName} onClick={() => void withdraw('whole', group.wholeRowId!)}>
                    {t('subSkill.remove')}
                  </button>
                </li>
              ) : null}
              {group.subSkills.map((row) => (
                <li key={row.id} data-person-sub-skill={row.subSkillName}>
                  <span>{line(row.subSkillName, row.notedByName ? `· ${t('subSkill.notedBy')} ${row.notedByName}` : '')}</span>
                  <button type="button" className={styles.clearFlag} data-withdraw-sub-skill={row.subSkillName} onClick={() => void withdraw('sub', row.id)}>
                    {t('subSkill.remove')}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <Select size={Size.Medium} label={t('skill.pickSkill')} value={skillId} onChange={pickSkill} placeholder={t('skill.pickSkillOne')} options={options.map((option) => ({ value: option.skillId, label: option.skillName }))} dataAttribute={{ 'data-skill-choice': 'true' }} />
      {/*
        🔒 The second level: the whole skill, or one sub-skill beneath it. A skill
        with no sub-skills configured offers the whole alone and says why, so an
        empty taxonomy reads as settled rather than as a list that failed to load.
      */}
      <Select
        size={Size.Medium}
        label={t('skill.pickWhat')}
        value={choice}
        onChange={setChoice}
        disabled={!chosenSkill}
        placeholder={t('skill.pickWhat')}
        options={[
          // A skill already held whole is not offered whole a second time; the row above withdraws it.
          ...(chosenSkill && !heldHere?.wholeRowId ? [{ value: WHOLE, label: t('skill.wholeSkill') }] : []),
          ...(chosenSkill?.subSkills ?? [])
            .filter((sub) => !(heldHere?.subSkills ?? []).some((row) => row.subSkillId === sub.id))
            .map((sub) => ({ value: sub.id, label: sub.name })),
        ]}
        dataAttribute={{ 'data-skill-level-choice': 'true' }}
      />
      {chosenSkill && chosenSkill.subSkills.length === 0 ? <p className={styles.hint}>{t('skill.wholeSkillOnly')}</p> : null}
    </Modal>
  );
}
